<template>
  <div>
    <p class="flex items-center gap-2 text-sm text-blue-700 mb-2">
      Bagi tagihan menjadi maksimal dua pembayaran. Pastikan sisa saldo menjadi
      nol sebelum menyimpan.
    </p>
    <Card class="my-card">
      <template #title>
        <h5 class="m-0">Detail Pembayaran</h5>
      </template>
      <template #content>
        <div class="mb-3">
          <strong>{{ form.sl_no }} / {{ form.sl_at }} / {{ form.src }}</strong>
        </div>

        <div class="grid mb-3">
          <!-- Grand Total -->
          <div class="col-6" style="width: 50%">
            <label class="block mb-1">Grand Total</label>
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">Rp</span>
              <InputNumber
                :model-value="form.dpp - form.return_value"
                class="w-full"
                input-class="w-full"
                :min="0"
                locale="id-ID"
                :min-fraction-digits="0"
                :max-fraction-digits="2"
                :allow-empty="false"
                readonly
              />
            </div>
          </div>

          <!-- Total Payment -->
          <div class="col-6" style="width: 50%">
            <label class="block mb-1">Total Payment</label>
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">Rp</span>
              <InputNumber
                :model-value="originalTotalPayment"
                class="w-full"
                input-class="w-full"
                :min="0"
                locale="id-ID"
                :min-fraction-digits="0"
                :max-fraction-digits="2"
                :allow-empty="false"
                readonly
              />
            </div>
          </div>
        </div>

        <!-- Payment #1 -->
        <div class="grid mb-3">
          <div class="col-6">
            <label>Nominal #1</label>
            <InputNumber
              v-model.number="form.payment_1"
              class="w-full"
              input-class="w-full"
              :min="0"
              locale="id-ID"
              :min-fraction-digits="0"
              :max-fraction-digits="2"
              :allow-empty="false"
              @input="onPayment1Input"
            />
          </div>
          <div class="col-2">
            <label>Tipe #1</label>
            <Dropdown
              v-model="form.member_1"
              :options="listPayment"
              class="w-full"
              input-class="w-full"
              optionValue="kode"
              optionLabel="name"
            />
          </div>
        </div>

        <!-- Split Button -->
        <div class="mb-3">
          <Button
            v-if="!splitActive"
            label="Split Payment"
            icon="pi pi-exchange"
            class="p-button-outlined w-full"
            @click="enableSplit"
            :disabled="form.cash_return == 0"
          />
        </div>

        <!-- Payment #2 jika splitActive -->
        <div v-if="splitActive" class="grid mb-3" style="align-items: flex-end">
          <div class="col-6">
            <label>Nominal #2</label>
            <InputNumber
              v-model.number="form.payment_2"
              class="w-full"
              input-class="w-full"
              :min="0"
              locale="id-ID"
              :min-fraction-digits="0"
              :max-fraction-digits="2"
              :allow-empty="false"
              @input="onPayment2Input"
            />
          </div>
          <div class="col-2">
            <label>Tipe #2</label>
            <Dropdown
              v-model="form.member_2"
              :options="listPayment"
              class="w-full"
              input-class="w-full"
              optionValue="kode"
              optionLabel="name"
            />
          </div>
          <div class="col-1 flex justify-center items-end">
            <Button
              icon="pi pi-minus"
              class="p-button-danger"
              @click="removeSplit"
            />
          </div>
        </div>

        <!-- Info sisa / kembalian (langsung update saat input) -->
        <div
          class="p-2 border-round payment-status"
          :class="{
            'status-kurang': paymentStatus === 'kurang',
            'status-pas': paymentStatus === 'pas',
            'status-lebih': paymentStatus === 'lebih',
          }"
          style="
            display: flex;
            justify-content: space-between;
            align-items: center;
          "
        >
          <div>
            <strong>
              <span v-if="paymentStatus === 'kurang'">Pembayaran kurang </span>
              <span v-else-if="paymentStatus === 'lebih'">Sisa </span>
              <span v-else>Sisa</span>
            </strong>
          </div>
          <div style="text-align: right">
            <strong>{{ formatCurrency(form.cash_return) }}</strong>
          </div>
        </div>
      </template>
    </Card>

    <div class="flex justify-content-end align-items-end gap-2 mt-3">
      <Button
        label="Tutup"
        icon="pi pi-times"
        @click="$emit('close')"
        class="p-button-text"
      />
      <Button
        :loading="loading"
        label="Simpan"
        icon="pi pi-check"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script>
import { formatCurrency } from '@/helpers/index'
import { nextTick } from 'vue'

export default {
  props: {
    loading: { type: Boolean, default: false },
    item: {
      type: Object,
      default() {
        return {}
      },
    },
    listPayment: {
      type: Array,
      default() {
        return []
      },
    },
  },
  data() {
    return {
      splitActive: false,
      originalTotalPayment: 0,

      // new internal states
      initialHasReturn: false,
      initialCashReturn: 0,
      initialTotalNominal: 0, // payment1 + payment2 when mounted
      adjustedForReturn: false, // apakah UI sudah di-adjust untuk memasukkan cash_return
    }
  },
  computed: {
    form() {
      return this.item
    },
    totalPayment() {
      return (
        (Number(this.form.payment_1) || 0) + (Number(this.form.payment_2) || 0)
      )
    },

    paymentStatus() {
      if (this.totalPayment < this.originalTotalPayment) return 'kurang'
      if (this.totalPayment === this.originalTotalPayment) return 'pas'
      return 'lebih'
    },
    shortage() {
      return Number(this.form.cash_return || 0)
    },
  },
  watch: {
    totalPayment: {
      immediate: true,
      handler(val) {
        this.form.cash_return = val - this.originalTotalPayment
      },
    },
  },
  mounted() {
    // normalize numeric fields
    this.form.dpp = Number(this.form.dpp || 0)
    this.form.payment_1 = Number(this.form.payment_1 ?? this.form.dpp ?? 0)
    this.form.payment_2 = Number(this.form.payment_2 || 0)
    this.form.return_value = Number(this.form.return_value || 0)
    this.form.cash_return = Number(this.form.cash_return || 0)

    // record initial states (used for rules about cash_return)
    this.initialCashReturn = Number(this.form.cash_return || 0)
    this.initialHasReturn = this.initialCashReturn > 0
    this.initialTotalNominal =
      Number(this.form.payment_1 || 0) + Number(this.form.payment_2 || 0)

    // default member_1 to CS only if empty
    if (!this.form.member_1) {
      const cs = this.listPayment.find((p) => p.kode?.toLowerCase() === 'cs')
      if (cs) this.form.member_1 = cs.kode
    }

    if (!this.form.member_2) {
      const cs = this.listPayment.find((p) => p.kode?.toLowerCase() === 'cs')
      if (cs) this.form.member_2 = cs.kode
    }

    // splitActive only if payment_2 > 0 or member_2 set
    if (Number(this.form.payment_2) > 0) {
      this.splitActive = true
    } else {
      this.splitActive = false
      // ensure we don't show payment_2 inputs
      this.form.payment_2 = Number(this.form.payment_2 || 0)
      this.form.member_2 = this.form.member_2.toUpperCase() || ''
    }

    this.originalTotalPayment =
      Number(this.form.payment_1 || 0) + Number(this.form.payment_2 || 0)

    // ensure return shows immediately
    this.recalculateReturn()
  },
  methods: {
    formatCurrency(val) {
      // reuse your helper; ensure it accepts negative numbers
      return formatCurrency(val)
    },

    onDppChange() {
      // if not split, keep payment_1 = dpp (as original behavior)
      // if (!this.splitActive) this.form.payment_1 = Number(this.form.dpp || 0)
      this.recalculateReturn()
    },

    onPaymentChange() {
      this.form.payment_1 = Number(this.form.payment_1 || 0)
      this.form.payment_2 = Number(this.form.payment_2 || 0)

      // if split and payment_2 becomes 0 -> remove split and set payment_1 back to dpp
      if (this.splitActive && Number(this.form.payment_2 || 0) <= 0) {
        this.removeSplit()
        return
      }

      this.recalculateReturn()
    },

    onPayment1Input(e) {
      const val = e?.value
      this.form.payment_1 =
        val != null && val !== '' ? Number(val) : 0

      if (this.splitActive && Number(this.form.payment_2 || 0) <= 0) {
        this.removeSplit()
        return
      }
      this.recalculateReturn()
    },

    onPayment2Input(e) {
      const val = e?.value
      this.form.payment_2 =
        val != null && val !== '' ? Number(val) : 0
      this.recalculateReturn()
    },

    enableSplit() {
      // if member_1 empty, set default CS (we also set on mounted)
      if (!this.form.member_1) {
        const cs = this.listPayment.find((p) => p.kode === 'cs')
        if (cs) this.form.member_1 = cs.kode
      }

      // require member_1 after attempting default
      if (!this.form.member_1) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validasi',
          detail: 'Payment method #1 harus diisi sebelum split.',
          life: 3000,
        })
        return
      }

      this.splitActive = true

      // Delay setting payment_2 and member_2 until DOM mounted to avoid PrimeVue InputNumber render issues
      nextTick(() => {
        const remain = Math.max(
          0,
          this.originalTotalPayment - Number(this.form.payment_1 || 0)
        )
        this.form.payment_2 = remain

        // Tentukan default tipe payment #2 hanya jika belum ada value
        if (!this.form.member_2) {
          if (this.form.member_1.toLowerCase() === 'cs') {
            // CS -> pilih first DB-like kode (startsWith 'D')
            const dbPayment = this.listPayment.find(
              (p) => p.kode &&  p.kode.startsWith('D')
            )
            this.form.member_2 = dbPayment ? dbPayment.kode : ''
          } else {
            // Non-cash -> default CS
            const csPayment = this.listPayment.find((p) => p.kode.toLowerCase() === 'cs')
            this.form.member_2 = csPayment ? csPayment.kode : ''
          }
        }

        this.recalculateReturn()
      })
    },

    removeSplit() {
      // When user removes payment_2, set payment_1 back to grand total (dpp)
      this.splitActive = false
      this.form.payment_2 = 0
      this.form.member_2 = ''
      // set payment_1 to grand total as requested (UI only)
      this.form.payment_1 = this.originalTotalPayment
      this.recalculateReturn()
    },

    recalculateReturn() {
      const total =
        Number(this.form.payment_1 || 0) + Number(this.form.payment_2 || 0)

      this.form.cash_return = total - this.originalTotalPayment

    },

    validateBeforeSave() {
      if (Number(this.form.cash_return || 0) !== 0) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validasi Total',
          detail: 'Sisa harus 0 sebelum menyimpan.',
          life: 3000,
        })
        return false
      }

      if (!this.form.member_1) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validasi',
          detail: 'Payment method #1 harus dipilih.',
          life: 3000,
        })
        return false
      }

      if (Number(this.form.payment_1 || 0) <= 0) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validasi',
          detail: 'Nominal #1 tidak boleh 0.',
          life: 3000,
        })
        return false
      }

      if (this.splitActive) {
        if (Number(this.form.payment_2 || 0) <= 0) {
          this.$toast.add({
            severity: 'error',
            summary: 'Validasi',
            detail: 'Nominal #2 tidak boleh 0.',
            life: 3000,
          })
          return false
        }
        if (!this.form.member_2) {
          this.$toast.add({
            severity: 'error',
            summary: 'Validasi',
            detail: 'Payment method #2 harus dipilih.',
            life: 3000,
          })
          return false
        }
        if (this.form.member_1 === this.form.member_2) {
          this.$toast.add({
            severity: 'error',
            summary: 'Validasi',
            detail: 'Payment method #2 harus berbeda.',
            life: 3000,
          })
          return false
        }
      }

      return true
    },

    handleSubmit() {
      this.recalculateReturn()

      if (!this.validateBeforeSave()) return

      const payload = { ...this.form }

      if (
        !this.adjustedForReturn &&
        this.initialHasReturn &&
        !this.splitActive
      ) {
        // user removed split and we didn't adjust UI earlier -> add cash_return to payment_1 for DB
        payload.payment_1 =
          Number(payload.payment_1 || 0) + Number(this.initialCashReturn || 0)
        // keep UI unchanged (we don't want to confuse user) – the UI remains showing payment_1 = grandTotal
      }

      // In other cases, adjustedForReturn true means UI already contains the transferred amount.
      // Ensure numeric types
      payload.payment_1 = Number(payload.payment_1 || 0)
      payload.payment_2 = Number(payload.payment_2 || 0)
      payload.cash_return = Number(payload.cash_return || 0)

      // Emit save with final payload for DB
      this.$emit('save', payload)
    },
  },
}
</script>

<style>
.my-card {
  border: 1px solid #dcdcdc;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  border-radius: 6px;
  background-color: #fff;
}

.status-kurang {
  background-color: #e53935;
  color: white;
}

.status-pas {
  background-color: #1e88e5;
  color: white;
}

.status-lebih {
  background-color: #43a047;
  color: white;
}

</style>
